import React, { useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Save, X, Calendar, Clock, Target, Trophy, CheckCircle, History } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/hooks/useAuth';
import { useBetting } from '@/hooks/useBetting';
import { Match, MatchStatus } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

/** Convert a datetime-local string (YYYY-MM-DDTHH:MM) to ISO 8601 preserving local time */
const localInputToISO = (localValue: string): string => {
  if (!localValue) return new Date().toISOString();
  // datetime-local value is already in local time; treat it as local
  const [datePart, timePart] = localValue.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = (timePart || '00:00').split(':').map(Number);
  const d = new Date(year, month - 1, day, hours, minutes, 0, 0);
  return d.toISOString();
};

// 3-Column Score Structure: Home | Draw | Away
const SCORE_OPTIONS = [
  // Home Win
  '1-0', '2-0', '2-1', '3-0', '3-1', '3-2', '4-0', '4-1', '4-2', '4-3',
  // Draw
  '0-0', '1-1', '2-2', '3-3', '4-4', '5-5',
  // Away Win
  '0-1', '0-2', '1-2', '0-3', '1-3', '2-3', '0-4', '1-4', '2-4', '3-4',
  // Any Other Score
  'AOS'
];

// Grouped score options for better UI
const GROUPED_SCORE_OPTIONS = {
  'Home Win': ['1-0', '2-0', '2-1', '3-0', '3-1', '3-2', '4-0', '4-1', '4-2', '4-3'],
  'Draw': ['0-0', '1-1', '2-2', '3-3', '4-4', '5-5'],
  'Away Win': ['0-1', '0-2', '1-2', '0-3', '1-3', '2-3', '0-4', '1-4', '2-4', '3-4'],
  'Other': ['AOS']
};

interface ScoreOdds {
  [key: string]: number;
}

interface MatchFormData {
  homeTeam: string;
  awayTeam: string;
  startTime: string;
  status: MatchStatus;
}

// Match Form Component - Separate component to prevent re-creation
interface MatchFormProps {
  isEditing: boolean;
  editingMatch?: Match | null;
  onClose: () => void;
  onSuccess: () => void;
}

const MatchForm: React.FC<MatchFormProps> = ({ isEditing, editingMatch, onClose, onSuccess }) => {
  const { addMatch, updateMatch } = useBetting();
  
  // Initialize form data
  const [formData, setFormData] = useState<MatchFormData>(() => ({
    homeTeam: editingMatch?.homeTeam || '',
    awayTeam: editingMatch?.awayTeam || '',
    startTime: editingMatch?.startTime
      ? (() => { const d = new Date(editingMatch.startTime); const pad = (n: number) => n.toString().padStart(2,'0'); return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; })()
      : '',
    status: editingMatch?.status || MatchStatus.UPCOMING
  }));

  // Initialize score odds
  const [scoreOdds, setScoreOdds] = useState<ScoreOdds>(() => {
    const initialOdds: ScoreOdds = {};
    SCORE_OPTIONS.forEach(score => {
      initialOdds[score] = editingMatch?.scoreOdds?.[score] || 0.00;
    });
    return initialOdds;
  });

  // Handle input changes with useCallback to prevent re-renders
  const handleInputChange = useCallback((field: keyof MatchFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Handle odds changes with useCallback
  const handleOddsChange = useCallback((score: string, value: string) => {
    console.log('MatchForm: handleOddsChange called', { score, value });
    // Handle empty string properly - keep it as empty, don't convert to 0 yet
    const numValue = value === '' ? '' : parseFloat(value);
    // Only convert to 0 if it's NaN (invalid number)
    const finalValue = numValue === '' ? 0 : (isNaN(numValue as number) ? 0 : numValue);
    console.log('MatchForm: Setting odds', { score, value, numValue, finalValue });
    setScoreOdds(prev => {
      const newOdds = {
        ...prev,
        [score]: finalValue
      };
      console.log('MatchForm: New scoreOdds state', newOdds);
      return newOdds;
    });
  }, []);

  // Save match
  const handleSaveMatch = useCallback(async () => {
    try {
      if (!formData.homeTeam || !formData.awayTeam || !formData.startTime) {
        toast.error('Please fill in all required fields');
        return;
      }

      // Convert the local datetime-local value back to ISO string preserving local time
      const isoStartTime = localInputToISO(formData.startTime);

      const matchData = {
        homeTeam: formData.homeTeam,
        awayTeam: formData.awayTeam,
        startTime: isoStartTime,
        status: formData.status,
        odds: {
          home: 0, // Legacy - keeping for compatibility
          away: 0,
          draw: 0
        },
        scoreOdds: scoreOdds // New score-based odds
      };

      if (isEditing && editingMatch) {
        await updateMatch(editingMatch.id, matchData);
        toast.success('Match updated successfully');
      } else {
        await addMatch({
          ...matchData,
          result: null
        });
        toast.success('Match created successfully');
      }

      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to save match');
      console.error('Save match error:', error);
    }
  }, [formData, scoreOdds, isEditing, editingMatch, addMatch, updateMatch, onSuccess, onClose]);

  return (
    <div className="space-y-6">
      {/* Basic Match Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="homeTeam">Home Team</Label>
          <Input
            id="homeTeam"
            value={formData.homeTeam}
            onChange={(e) => handleInputChange('homeTeam', e.target.value)}
            placeholder="Enter home team name"
            autoComplete="off"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="awayTeam">Away Team</Label>
          <Input
            id="awayTeam"
            value={formData.awayTeam}
            onChange={(e) => handleInputChange('awayTeam', e.target.value)}
            placeholder="Enter away team name"
            autoComplete="off"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="startTime">Start Time</Label>
          <Input
            id="startTime"
            type="datetime-local"
            value={formData.startTime}
            onChange={(e) => handleInputChange('startTime', e.target.value)}
            autoComplete="off"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value as MatchStatus)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={MatchStatus.UPCOMING}>Upcoming</SelectItem>
              <SelectItem value={MatchStatus.LIVE}>Live</SelectItem>
              <SelectItem value={MatchStatus.COMPLETED}>Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Score-Based Odds - 3 Column Layout */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold mb-2">Score-Based Odds</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Set odds for each possible score outcome. Use the 3-column layout: Home Win | Draw & AOS | Away Win
          </p>
        </div>
        
        {/* Column Headers */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-lg text-center">
            <div className="text-sm font-semibold text-blue-700 dark:text-blue-300">HOME WIN</div>
            <div className="text-xs text-blue-600 dark:text-blue-400">{formData.homeTeam || 'Home Team'}</div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-center">
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">DRAW & AOS</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">Equal Scores + Any Other</div>
          </div>
          <div className="bg-red-50 dark:bg-red-950 p-3 rounded-lg text-center">
            <div className="text-sm font-semibold text-red-700 dark:text-red-300">AWAY WIN</div>
            <div className="text-xs text-red-600 dark:text-red-400">{formData.awayTeam || 'Away Team'}</div>
          </div>
        </div>

        {/* 3-Column Odds Grid */}
        <div className="grid grid-cols-3 gap-4">
          {/* HOME WIN COLUMN */}
          <div className="space-y-3">
            <div className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">Home Win Scores</div>
            {GROUPED_SCORE_OPTIONS['Home Win'].map((score) => (
              <div key={score} className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label htmlFor={`odds-${score}`} className="text-sm font-medium">
                    {score}
                  </Label>
                  <span className="text-xs text-muted-foreground">odds</span>
                </div>
                <Input
                  id={`odds-${score}`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={scoreOdds[score] || ''}
                  onChange={(e) => handleOddsChange(score, e.target.value)}
                  placeholder="0.00"
                  className="text-center bg-blue-50/50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800"
                  autoComplete="off"
                />
              </div>
            ))}
          </div>

          {/* DRAW & AOS COLUMN */}
          <div className="space-y-3">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Draw & AOS Scores</div>
            {/* Draw Scores */}
            {GROUPED_SCORE_OPTIONS['Draw'].map((score) => (
              <div key={score} className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label htmlFor={`odds-${score}`} className="text-sm font-medium">
                    {score}
                  </Label>
                  <span className="text-xs text-muted-foreground">odds</span>
                </div>
                <Input
                  id={`odds-${score}`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={scoreOdds[score] || ''}
                  onChange={(e) => handleOddsChange(score, e.target.value)}
                  placeholder="0.00"
                  className="text-center bg-gray-50/50 dark:bg-gray-800/30 border-gray-200 dark:border-gray-700"
                  autoComplete="off"
                />
              </div>
            ))}
            
            {/* AOS */}
            {GROUPED_SCORE_OPTIONS['Other'].map((score) => (
              <div key={score} className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label htmlFor={`odds-${score}`} className="text-sm font-medium">
                    {score === 'AOS' ? 'Any Other Score' : score}
                  </Label>
                  <span className="text-xs text-muted-foreground">odds</span>
                </div>
                <Input
                  id={`odds-${score}`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={scoreOdds[score] || ''}
                  onChange={(e) => handleOddsChange(score, e.target.value)}
                  placeholder="0.00"
                  className="text-center bg-purple-50/50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800"
                  autoComplete="off"
                />
              </div>
            ))}
          </div>

          {/* AWAY WIN COLUMN */}
          <div className="space-y-3">
            <div className="text-sm font-medium text-red-700 dark:text-red-300 mb-2">Away Win Scores</div>
            {GROUPED_SCORE_OPTIONS['Away Win'].map((score) => (
              <div key={score} className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label htmlFor={`odds-${score}`} className="text-sm font-medium">
                    {score}
                  </Label>
                  <span className="text-xs text-muted-foreground">odds</span>
                </div>
                <Input
                  id={`odds-${score}`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={scoreOdds[score] || ''}
                  onChange={(e) => handleOddsChange(score, e.target.value)}
                  placeholder="0.00"
                  className="text-center bg-red-50/50 dark:bg-red-950/30 border-red-200 dark:border-red-800"
                  autoComplete="off"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2 pt-4 border-t">
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={() => {
              const defaultOdds: ScoreOdds = {};
              SCORE_OPTIONS.forEach(score => {
                // Set reasonable default odds
                if (score === '1-1' || score === '0-0') defaultOdds[score] = 4.5;
                else if (score === '2-1' || score === '1-2') defaultOdds[score] = 7.0;
                else if (score === '2-0' || score === '0-2') defaultOdds[score] = 9.0;
                else if (score === 'AOS') defaultOdds[score] = 12.0;
                else defaultOdds[score] = 15.0 + Math.random() * 20;
              });
              setScoreOdds(defaultOdds);
              toast.success('Default odds applied');
            }}
          >
            Set Default Odds
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={() => {
              setScoreOdds({});
              toast.success('All odds cleared');
            }}
          >
            Clear All Odds
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={() => {
              const filledCount = Object.values(scoreOdds).filter(v => v > 0).length;
              toast.info(`${filledCount} out of ${SCORE_OPTIONS.length} odds set`);
            }}
          >
            Check Progress ({Object.values(scoreOdds).filter(v => v > 0).length}/{SCORE_OPTIONS.length})
          </Button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={onClose}>
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
        <Button onClick={handleSaveMatch}>
          <Save className="w-4 h-4 mr-2" />
          {isEditing ? 'Update Match' : 'Create Match'}
        </Button>
      </div>
    </div>
  );
};

/**
 * Match Management Page - Company users can add/edit matches with score-based odds
 */
const MatchManagement: React.FC = () => {
  const { user, isCompany } = useAuth();
  const { matches, deleteMatch, submitMatchResult } = useBetting();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingMatch, setEditingMatch] = useState<Match | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [resultEntryMatch, setResultEntryMatch] = useState<Match | null>(null);
  const [selectedResult, setSelectedResult] = useState<string>('');
  const [isSubmittingResult, setIsSubmittingResult] = useState(false);

  // Handle edit match
  const handleEditMatch = useCallback((match: Match) => {
    setEditingMatch(match);
  }, []);

  // Handle delete match
  const handleDeleteMatch = useCallback(async (matchId: string) => {
    try {
      await deleteMatch(matchId);
      toast.success('Match deleted successfully');
    } catch (error) {
      toast.error('Failed to delete match');
      console.error('Delete match error:', error);
    }
  }, [deleteMatch]);

  // Handle form success (refresh data)
  const handleFormSuccess = useCallback(() => {
    // Data will be automatically refreshed by the useBetting hook
  }, []);

  // Handle close dialogs
  const handleCloseAddDialog = useCallback(() => {
    setIsAddDialogOpen(false);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setEditingMatch(null);
  }, []);

  // Handle result entry
  const handleEnterResult = useCallback((match: Match) => {
    setResultEntryMatch(match);
    setSelectedResult('');
  }, []);

  const handleCloseResultDialog = useCallback(() => {
    setResultEntryMatch(null);
    setSelectedResult('');
    setIsSubmittingResult(false);
  }, []);

  const handleSubmitResult = useCallback(async () => {
    if (!resultEntryMatch || !selectedResult) {
      toast.error('Please select a result');
      return;
    }

    setIsSubmittingResult(true);
    try {
      const result = await submitMatchResult(resultEntryMatch.id, selectedResult);
      
      if (result.isResettlement) {
        toast.success(`Match re-settled! Reverted ${result.revertedBets || 0} old bets. New results: ${result.wonBets} won, ${result.lostBets} lost. Total winnings: RM ${result.totalWinnings?.toFixed(2) || '0.00'}`);
      } else {
        toast.success(`Match result submitted! ${result.wonBets} winning bets, ${result.lostBets} losing bets. Total winnings: RM ${result.totalWinnings?.toFixed(2) || '0.00'}`);
      }
      
      handleCloseResultDialog();
    } catch (error) {
      console.error('Failed to submit result:', error);
      toast.error('Failed to submit result: ' + (error as Error).message);
    } finally {
      setIsSubmittingResult(false);
    }
  }, [resultEntryMatch, selectedResult, submitMatchResult]);


  // Split matches: active (future) vs history (past start time or completed)
  const activeMatches = useMemo(() => {
    const now = new Date();
    return matches
      .filter(m => {
        const start = new Date(m.startTime);
        const isActiveStatus = m.status === MatchStatus.UPCOMING || m.status === MatchStatus.LIVE;
        return isActiveStatus && start > now;
      })
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }, [matches]);

  const historyMatches = useMemo(() => {
    const now = new Date();
    return matches
      .filter(m => {
        const start = new Date(m.startTime);
        const isDoneStatus = m.status === MatchStatus.COMPLETED || m.status === MatchStatus.CANCELLED;
        return isDoneStatus || start <= now;
      })
      .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
  }, [matches]);

  // Memoize matches list to prevent unnecessary re-renders
  const matchesList = useMemo(() => {
    const list = showHistory ? historyMatches : activeMatches;
    return list.map((match) => (
      <motion.div
        key={match.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  {match.matchNumber && (
                    <Badge variant="outline" className="font-mono text-xs bg-primary/10 text-primary border-primary/30">
                      {match.matchNumber}
                    </Badge>
                  )}
                  {match.homeTeam} vs {match.awayTeam}
                </CardTitle>
                <CardDescription className="flex items-center gap-4 mt-1">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(match.startTime).toLocaleDateString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {new Date(match.startTime).toLocaleTimeString()}
                  </span>
                  <Badge variant={match.status === MatchStatus.UPCOMING ? 'secondary' : match.status === MatchStatus.LIVE ? 'default' : 'outline'}>
                    {match.status}
                  </Badge>
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEditMatch(match)}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEnterResult(match)}
                  className={match.status === MatchStatus.COMPLETED ? "text-orange-600 hover:text-orange-700" : "text-green-600 hover:text-green-700"}
                  title={match.status === MatchStatus.COMPLETED ? "Re-set final score" : "Set final score"}
                >
                  <Trophy className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteMatch(match.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">
                Score-based odds system configured
              </div>
              {match.result && (
                <div className="flex items-center gap-2 text-sm">
                  <Trophy className="w-4 h-4 text-green-600" />
                  <span className="font-semibold">Final Score:</span>
                  <span className="font-mono font-bold text-green-600">{match.result}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    ));
  }, [matches, handleEditMatch, handleDeleteMatch, showHistory, activeMatches, historyMatches]);

  if (!isCompany()) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Access denied. Company users only.</p>
        </div>
      </Layout>
    );
  }

  console.log('MatchManagement render - isCompany:', isCompany(), 'user:', user?.tier);
  
  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {showHistory ? '📋 Match History' : 'Match Management'}
            </h1>
            <p className="text-muted-foreground">
              {showHistory
                ? `${historyMatches.length} completed / past match${historyMatches.length !== 1 ? 'es' : ''}`
                : 'Create and manage football matches with score-based odds'}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant={showHistory ? 'default' : 'outline'}
              onClick={() => setShowHistory(h => !h)}
            >
              <History className="mr-2 h-4 w-4" />
              {showHistory ? '← Back to Active' : 'Match History'}
            </Button>

            {!showHistory && (
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Match
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Match</DialogTitle>
                <DialogDescription>
                  Create a new football match with score-based odds
                </DialogDescription>
              </DialogHeader>
              <MatchForm 
                isEditing={false} 
                onClose={handleCloseAddDialog}
                onSuccess={handleFormSuccess}
              />
              </DialogContent>
            </Dialog>
            )}
          </div>
        </div>

        {/* Matches List */}
        <div className="grid gap-4">
          {(showHistory ? historyMatches : activeMatches).length === 0 ? (
            <Card>
              <CardContent className="flex items-center justify-center h-32">
                <p className="text-muted-foreground">
                  {showHistory ? 'No completed matches yet.' : 'No active matches. Create your first match!'}
                </p>
              </CardContent>
            </Card>
          ) : (
            matchesList
          )}
        </div>

        {/* Edit Match Dialog */}
        <Dialog open={!!editingMatch} onOpenChange={(open) => !open && handleCloseEditDialog()}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Match</DialogTitle>
              <DialogDescription>
                Update match details and score-based odds
              </DialogDescription>
            </DialogHeader>
            {editingMatch && (
              <MatchForm 
                isEditing={true} 
                editingMatch={editingMatch}
                onClose={handleCloseEditDialog}
                onSuccess={handleFormSuccess}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Result Entry Dialog */}
        <Dialog open={!!resultEntryMatch} onOpenChange={(open) => !open && handleCloseResultDialog()}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trophy className={resultEntryMatch?.status === MatchStatus.COMPLETED ? "w-5 h-5 text-orange-600" : "w-5 h-5 text-green-600"} />
                {resultEntryMatch?.status === MatchStatus.COMPLETED ? 'Re-set Match Result' : 'Enter Match Result'}
              </DialogTitle>
              <DialogDescription>
                {resultEntryMatch && (
                  <div className="space-y-2">
                    <span>
                      {resultEntryMatch.homeTeam} vs {resultEntryMatch.awayTeam}
                    </span>
                    {resultEntryMatch.status === MatchStatus.COMPLETED && resultEntryMatch.result && (
                      <div className="flex items-center gap-2 p-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-md">
                        <Trophy className="w-4 h-4 text-orange-600" />
                        <div className="text-sm">
                          <span className="font-semibold text-orange-900 dark:text-orange-100">Current Result: </span>
                          <span className="font-mono font-bold text-orange-600">{resultEntryMatch.result}</span>
                        </div>
                      </div>
                    )}
                    {resultEntryMatch.status === MatchStatus.COMPLETED && (
                      <div className="text-sm text-orange-600 dark:text-orange-400 font-medium">
                        ⚠️ Warning: Re-setting the result will revert previous winnings and recalculate all bets for this match.
                      </div>
                    )}
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="result-select">Final Score</Label>
                <Select value={selectedResult} onValueChange={setSelectedResult}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select final score" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(GROUPED_SCORE_OPTIONS).map(([category, scores]) => (
                      <div key={category}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                          {category}
                        </div>
                        {scores.map((score) => (
                          <SelectItem key={score} value={score} className="pl-4">
                            {score === 'AOS' ? 'Any Other Score (AOS)' : score}
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={handleCloseResultDialog}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmitResult}
                  disabled={!selectedResult || isSubmittingResult}
                  className={resultEntryMatch?.status === MatchStatus.COMPLETED ? "bg-orange-600 hover:bg-orange-700" : "bg-green-600 hover:bg-green-700"}
                >
                  {isSubmittingResult ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {resultEntryMatch?.status === MatchStatus.COMPLETED ? 'Re-settling Bets...' : 'Settling Bets...'}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {resultEntryMatch?.status === MatchStatus.COMPLETED ? 'Re-submit Result' : 'Submit Result'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Result Entry Dialog */}
        <Dialog open={!!resultEntryMatch} onOpenChange={(open) => !open && handleCloseResultDialog()}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-green-600" />
                Enter Match Result
              </DialogTitle>
              <DialogDescription>
                {resultEntryMatch && (
                  <span>
                    {resultEntryMatch.homeTeam} vs {resultEntryMatch.awayTeam}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="result-select">Final Score</Label>
                <Select value={selectedResult} onValueChange={setSelectedResult}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select final score" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(GROUPED_SCORE_OPTIONS).map(([category, scores]) => (
                      <div key={category}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                          {category}
                        </div>
                        {scores.map((score) => (
                          <SelectItem key={score} value={score} className="pl-4">
                            {score === 'AOS' ? 'Any Other Score (AOS)' : score}
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={handleCloseResultDialog}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmitResult}
                  disabled={!selectedResult || isSubmittingResult}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isSubmittingResult ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Settling Bets...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Submit Result
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default MatchManagement;